import {
  Alert,
  Button,
  Col,
  Drawer,
  Dropdown,
  Form,
  Grid,
  message,
  Modal,
  PageHeader,
  Row,
  Typography,
  Space,
} from "antd";
import { Panel, PanelGroup } from "react-resizable-panels";
import { SettingFilled } from "@ant-design/icons";
import CandidateListing from "features/candidates/candidateListing/candidateListing";
import ViewActivity from "features/candidates/viewActivity";
import LayoutCss from "layout/layout.module.scss";
import { groupBy, map, orderBy } from "lodash";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useParams } from "react-router-dom";
import Job from "redux/models/job";
import LookupTable from "redux/models/lookupTable";
import Status from "redux/models/status";
import { selectConfigration } from "redux/slices/configrationSlice";
import { selectUserRoles } from "redux/slices/userSlice";
import K from "utilities/constants";
import { isPermissionPresent } from "utilities/generalUtility";
import { setCandidateUdf } from "redux/slices/udfSlice";
import UDF from "redux/models/udf";
import ChangeCandidateStatus from "features/candidates/changeCandidateStatus";
import { DataTableLayout } from "common/components/dataTableLayout/dataTableLayout";
import { usePageFilter } from "hooks/usePageFilters";
import {
  bulkEditCandidateList,
  editCandidateList,
  removeFromCandidateList,
} from "redux/slices/candidateSlice";
import { PanelResizeDivider } from "common/components/panelResizeDivider/panelResizeDivider";
import { useIsAssignedToJob } from "features/candidates/hooks/useIsAssignedToJob";
import CancelJobForm from "../../details/jobs/cancelJobForm";
import JobSettingModal from "../../details/jobs/jobSettingsModal";
import styles from "../clientsJob.module.scss";
import "../jobCard.scss";
import SubmitCancelBillingForm from "../../details/jobs/submitCancelBilling";
import JobStatistics from "../jobStatistics";
import "../job.scss";
import { AddCandidateButton } from "./addCandidateButton/addCandidateButton";
import { JobDetails } from "../jobDetails";
import DeleteCandidateButton from "./deleteCandidateButton/deleteCandidateButton";
import TransferCandidateButton from "./transferCandidateButton/transferCandidateButton";

const { Title, Text } = Typography;
const { Lookup } = K.Network.URL;

export default function ClientJobs() {
  const gridRef = useRef();
  const screens = Grid.useBreakpoint();

  const history = useHistory();
  const dispatch = useDispatch();
  const { clientId, jobId } = useParams();

  const [cancelJobForm] = Form.useForm();
  const [cancelBillingForm] = Form.useForm();

  const [search, setSearch] = usePageFilter(`jobs-${jobId}-search`);

  const [isLoading, setIsLoading] = useState(false);

  const userRoles = useSelector(selectUserRoles);

  const [candidateActivity, setCandidateActivity] = useState(null);
  const [modalVisible, setModalVisible] = useState({
    editCandidateId: null,
    editCandidateEntryId: null,
    isSettingModalVisible: false,
    isJobCancelModalVisible: false,
    isCandidateModalVisible: false,
    isCancelBillingModalVisible: false,
    isCandidateStatusChangeModalVisible: false,
  });
  const [jobData, setJobData] = useState({
    jobDetails: null,
    statistics: null,
    status: [],
    subStatus: {},
    settings: null,
    shouldUpdateListing: false,
    moveCandidates: false,
  });
  const isAssignedToJob = useIsAssignedToJob(jobData.jobDetails);

  const jobStatus = {
    open: "open-flag",
    closed: "closed-flag",
    "on hold": "onhold-flag",
    custom: "blue-flag",
  };

  const [selectedCandidate, setSelectedCandidate] = useState({
    candidateJobEntryIds: [],
  });

  const lookupTables = useSelector(selectConfigration).lookup;

  const displayAssignedTooltip = !isAssignedToJob;

  const refreshCandidateListingTable = (row = null) => {
    if (gridRef?.current?.api) {
      if (row) {
        gridRef.current.api.applyServerSideTransaction({
          update: [row],
        });

        gridRef.current.api.refreshCells({ force: true });
      } else {
        gridRef.current.api.refreshServerSideStore({ purge: false });
      }
    }
  };

  const showModalCancelJob = () => {
    setModalVisible((prev) => {
      return { ...prev, isJobCancelModalVisible: true };
    });
  };

  const showModalCancelBilling = () => {
    setModalVisible((prev) => {
      return { ...prev, isCancelBillingModalVisible: true };
    });
  };
  const showModalChangeCandidateStatus = () => {
    setModalVisible((prev) => {
      return { ...prev, isCandidateStatusChangeModalVisible: true };
    });
  };

  const handleOkCancelJob = () => {
    cancelJobForm.resetFields();
    refreshCandidateListingTable();
    setModalVisible((prev) => {
      return { ...prev, isJobCancelModalVisible: false };
    });
  };

  const handleCancelJobCancel = () => {
    cancelJobForm.resetFields();
    setModalVisible((prev) => {
      return { ...prev, isJobCancelModalVisible: false };
    });
  };

  const handleCancelBilling = () => {
    cancelBillingForm.resetFields();
    setModalVisible((prev) => {
      return { ...prev, isCancelBillingModalVisible: false };
    });
  };
  const handleCancelBillingOk = () => {
    cancelBillingForm.resetFields();
    setModalVisible((prev) => {
      return { ...prev, isCancelBillingModalVisible: false };
    });
  };

  const getJobStatus = async () => {
    try {
      await dispatch(LookupTable.getData(K.Redux.JobStatus, Lookup.JobStatus));
    } catch (err) {
      console.error(err);
    }
  };
  const getStatusBreakdown = async () => {
    try {
      const res = await Status.getSubStatusBreakdown(jobId);
      const subStatus = groupBy(res, (item) => item.status.name);
      const status = map(subStatus, (item) => {
        const total = item.reduce(
          (prev, curr) => curr.candidatesToSubStatusCount + prev,
          0,
        );
        return { id: item[0].status.id, name: item[0].status.name, total };
      });

      setJobData((prev) => ({ ...prev, status, subStatus }));
    } catch (err) {
      console.error(err);
    }
  };

  const getJobStatistics = async () => {
    try {
      const res = await Job.v2.getStatistics(jobId);
      setJobData((prev) => ({ ...prev, statistics: res.data }));
    } catch (err) {
      console.error(err);
    }
  };

  const getJobDetails = async () => {
    try {
      const res = await Job.getById(clientId, jobId);

      setJobData((prev) => ({
        ...prev,
        jobDetails: {
          ...res.jobDetail,
          vacancies: res.jobDetail.vacancies.at(-1),
        },
        settings: {
          history: res.jobDetail.vacancies,
          setting: res.jobDetail.vacancies.at(-1),
        },
      }));
    } catch (err) {
      console.error(err);
    }
  };

  const onStatusChange = async (e) => {
    const statusId = e.key;

    if (statusId === undefined) {
      return null;
    }
    const body = {
      jobSourceId: jobId,
      jobStatusesId: statusId,
    };

    try {
      await Job.updateJobStatus(body);
      getJobDetails();
      message.success("Job Status Updated Successfully.");
    } catch (err) {
      console.error(err);
      message.error("Failed to Updated Job Status.");
    }
  };

  const getCandidateUdfs = async (entityKeyId = null, loading = true) => {
    const candidateUdfs = await UDF.getEditUdfFields(
      {
        clientId,
        entityId: K.UDF.Entities.Candidate.id,
        entityKey: K.UDF.Entities.Candidate.key,
        entityKeyId,
        isVisible: 1,
      },
      loading,
    );

    dispatch(setCandidateUdf(candidateUdfs));
    return candidateUdfs;
  };

  useEffect(() => {
    getJobStatus();
  }, [clientId, jobId]);

  useEffect(() => {
    (async () => {
      await Promise.all([
        getJobDetails(),
        getStatusBreakdown(),
        getJobStatistics(),
      ]);
    })();
  }, [jobId, clientId, jobData.shouldUpdateListing]);

  const getJobStatusName = (jobStatusId) => {
    return (
      lookupTables.jobStatus?.find((item) => item.id === jobStatusId)?.name ??
      K.NullPlaceholder
    );
  };
  const getJobStatusColor = (jobStatusId) => {
    const status = getJobStatusName(jobStatusId).toLowerCase();
    const statusColor = ["open", "closed", "on hold"].includes(status)
      ? status
      : "custom";

    return statusColor;
  };

  const [dropdownVisible, setDropdownVisible] = useState(false);

  const toggleDropdown = () => {
    setDropdownVisible(!dropdownVisible);
  };

  const [expanded, setExpanded] = useState(false);

  const handleToggle = (e) => {
    e.preventDefault();
    setExpanded(!expanded);
  };

  const showDrawer = () => {
    setModalVisible((prev) => {
      return { ...prev, isSettingModalVisible: true };
    });
  };

  const onClose = () => {
    setModalVisible((prev) => {
      return { ...prev, isSettingModalVisible: false };
    });
  };

  const onStatusChanged = async (updatedCandidate, declinedCandidates = []) => {
    dispatch(bulkEditCandidateList([updatedCandidate, ...declinedCandidates]));
    await Promise.all([getStatusBreakdown(), getJobDetails()]);
    refreshCandidateListingTable(updatedCandidate);
    declinedCandidates.forEach((candidate) => {
      refreshCandidateListingTable(candidate);
    });
  };

  const updateJobData = () => {
    setJobData((prev) => ({
      ...prev,
      shouldUpdateListing: !prev.shouldUpdateListing,
    }));
  };

  const onCandidateUpdated = (candidate) => {
    setCandidateActivity(candidate);
    dispatch(editCandidateList(candidate));
    refreshCandidateListingTable(candidate);
  };

  const onCandidateDeleted = (id) => {
    setCandidateActivity(null);
    updateJobData();
    gridRef.current.api.applyServerSideTransaction({
      remove: [{ id }],
    });
  };

  const onCandidateTransferred = () => {
    setCandidateActivity(null);
    updateJobData();
    dispatch(editCandidateList({ ...candidateActivity, isConsiderable: 0 }));
    refreshCandidateListingTable();
  };

  return (
    <>
      <div className={`${styles.wrapPageHeader} job-header-wrap`}>
        <PageHeader
          className={[
            LayoutCss.appPageHeader,
            `${LayoutCss.clientsJobPageHeader} client-page-head`,
          ]}
          ghost={false}
          onBack={() => history.goBack()}
          title={
            <>
              <Text>{jobData.jobDetails?.title}</Text>
              <Text
                className={
                  jobStatus[
                    getJobStatusColor(jobData?.jobDetails?.jobStatusesId)
                  ]
                }
              >
                <Dropdown
                  menu={{
                    items: lookupTables.jobStatus.map(({ id, name }) => ({
                      label: name,
                      key: id,
                    })),
                    onClick: onStatusChange,
                    disabled: !isPermissionPresent(
                      K.Permissions.EditJobStatus,
                      userRoles,
                    ),
                  }}
                  trigger={["click"]}
                  placement="right"
                  className="job-options-dropdown"
                >
                  <a onClick={toggleDropdown}>
                    {
                      lookupTables.jobStatus?.find(
                        (item) =>
                          item.id === jobData?.jobDetails?.jobStatusesId,
                      )?.name
                    }
                    {dropdownVisible ? (
                      <i className="icon-Vector" />
                    ) : (
                      <i className="icon-dropdown-arrow" />
                    )}
                  </a>
                </Dropdown>
              </Text>
            </>
          }
          subTitle={
            <div className="subtitle-wrap">
              <p> {jobData?.jobDetails?.jobLocation?.name}</p>
              <span>-</span>
              <a className="collapse-title-wrap" onClick={handleToggle}>
                {expanded ? "Collapse Detail" : "Expand Detail"}
              </a>
            </div>
          }
          extra={
            <>
              {jobData?.jobDetails?.isCancel !== 0 && (
                <div className="alert-wrap">
                  <Alert
                    showIcon
                    type="warning"
                    message={
                      <>
                        Cancel Reason:&nbsp;
                        <Text strong>
                          {jobData?.jobDetails?.cancelledReason ??
                            K.NullPlaceholder}
                        </Text>
                        <br />
                        <a onClick={showModalCancelBilling}>
                          Submit Cancellation Fee
                        </a>
                      </>
                    }
                  />
                </div>
              )}
              {isPermissionPresent(
                K.Permissions.EditJobOpenings,
                userRoles,
              ) && (
                <Button onClick={showDrawer} className={styles.bottomCardIcon}>
                  <SettingFilled />
                </Button>
              )}
            </>
          }
        >
          {expanded && <JobDetails details={jobData.jobDetails} />}
        </PageHeader>
      </div>
      <JobStatistics styles={styles} jobData={jobData} />
      <div className={`job-card-wrap ${styles.jobCardWrap}`}>
        {screens.xl ? (
          <PanelGroup
            direction="horizontal"
            autoSaveId="job-details-view-activity"
          >
            <Panel id="job-table" defaultSize={58} minSize={30}>
              <DataTableLayout
                style={{ height: "100%" }}
                className={
                  candidateActivity ? "border-radius-right-none" : undefined
                }
                searchDefaultValue={search}
                onSearchChange={setSearch}
                actions={[
                  {
                    permissions: [K.Permissions.CancelJob],
                    renderer: () => (
                      <Col>
                        <Button
                          danger
                          onClick={showModalCancelJob}
                          disabled={jobData.jobDetails?.isCancel ?? true}
                        >
                          Cancel Job
                        </Button>
                      </Col>
                    ),
                  },
                  {
                    hide: !isAssignedToJob,
                    permissions: [K.Permissions.CreateCandidate],
                    renderer: () => (
                      <Col>
                        <AddCandidateButton
                          disabled={jobData.jobDetails?.isCancel ?? true}
                          displayAssignedTooltip={displayAssignedTooltip}
                          getStatusBreakdown={getStatusBreakdown}
                          getCandidateUdf={getCandidateUdfs}
                          refreshTable={refreshCandidateListingTable}
                        />
                      </Col>
                    ),
                  },
                  {
                    invertPermission: true,
                    allowSuperAdmin: false,
                    renderer: () => (
                      <Col>
                        <Button
                          type="primary"
                          onClick={showModalChangeCandidateStatus}
                          disabled={
                            selectedCandidate.candidateJobEntryIds.length <= 0
                          }
                        >
                          Change Candidate Status
                        </Button>
                      </Col>
                    ),
                  },
                ]}
              >
                <CandidateListing
                  setCandidateActivity={setCandidateActivity}
                  setModalVisible={setModalVisible}
                  gridRef={gridRef}
                  search={search}
                  setSelectedData={setSelectedCandidate}
                  refreshTable={refreshCandidateListingTable}
                />
              </DataTableLayout>
            </Panel>
            {candidateActivity && (
              <>
                <PanelResizeDivider />
                <Panel id="job-activity" defaultSize={42} minSize={30}>
                  <ViewActivity
                    className="border-radius-left-none"
                    styles={styles}
                    clientId={clientId}
                    candidateActivity={candidateActivity}
                    setCandidateActivity={setCandidateActivity}
                    jobDetails={jobData.jobDetails}
                    setJobData={setJobData}
                    showEngagementTablePreview
                    onCancel={() => setCandidateActivity(null)}
                    onStatusChanged={onStatusChanged}
                    onCandidateChanged={onCandidateUpdated}
                    renderHeaderActions={(props) => (
                      <Space size={0}>
                        <DeleteCandidateButton
                          candidateActivity={candidateActivity}
                          jobDetails={props.jobDetails}
                          userSlice={props.userSlice}
                          styles={props.styles}
                          disabled={false}
                          onSuccess={onCandidateDeleted}
                        />

                        <TransferCandidateButton
                          candidateActivity={candidateActivity}
                          jobDetails={props.jobDetails}
                          clientId={clientId}
                          disabled={
                            [
                              K.CandidateStatus.Hired,
                              K.CandidateStatus.Terminated,
                              K.CandidateStatus.Withdrew,
                              K.CandidateStatus.Declined,
                            ].includes(props.statusHistory.previousStatus) ||
                            (props.jobDetails?.isCancel ?? true) ||
                            candidateActivity.isConsiderable === 0
                          }
                          onSuccess={onCandidateTransferred}
                        />
                      </Space>
                    )}
                  />
                </Panel>
              </>
            )}
          </PanelGroup>
        ) : (
          <Row gutter={[16, 16]}>
            <Col xs={24} xl={candidateActivity ? 14 : 24}>
              <DataTableLayout
                style={{ height: "100%" }}
                searchDefaultValue={search}
                onSearchChange={setSearch}
                actions={[
                  {
                    permissions: [K.Permissions.CancelJob],
                    renderer: () => (
                      <Col>
                        <Button
                          danger
                          onClick={showModalCancelJob}
                          disabled={jobData.jobDetails?.isCancel ?? true}
                        >
                          Cancel Job
                        </Button>
                      </Col>
                    ),
                  },
                  {
                    hide: !isAssignedToJob,
                    permissions: [K.Permissions.CreateCandidate],
                    renderer: () => (
                      <Col>
                        <AddCandidateButton
                          disabled={jobData.jobDetails?.isCancel ?? true}
                          displayAssignedTooltip={displayAssignedTooltip}
                          getStatusBreakdown={getStatusBreakdown}
                          getCandidateUdf={getCandidateUdfs}
                          refreshTable={refreshCandidateListingTable}
                        />
                      </Col>
                    ),
                  },
                  {
                    invertPermission: true,
                    allowSuperAdmin: false,
                    renderer: () => (
                      <Col>
                        <Button
                          type="primary"
                          onClick={showModalChangeCandidateStatus}
                          disabled={
                            selectedCandidate.candidateJobEntryIds.length <= 0
                          }
                        >
                          Change Candidate Status
                        </Button>
                      </Col>
                    ),
                  },
                ]}
              >
                <CandidateListing
                  setCandidateActivity={setCandidateActivity}
                  setModalVisible={setModalVisible}
                  gridRef={gridRef}
                  search={search}
                  refreshTable={refreshCandidateListingTable}
                  setSelectedData={setSelectedCandidate}
                />
              </DataTableLayout>
            </Col>

            {candidateActivity && (
              <Col xs={24} xl={10}>
                <ViewActivity
                  styles={styles}
                  clientId={clientId}
                  candidateActivity={candidateActivity}
                  setCandidateActivity={setCandidateActivity}
                  jobDetails={jobData.jobDetails}
                  setJobData={setJobData}
                  showEngagementTablePreview
                  onCancel={() => setCandidateActivity(null)}
                  onStatusChanged={onStatusChanged}
                  onCandidateChanged={onCandidateUpdated}
                  renderHeaderActions={(props) => (
                    <Space size={0}>
                      <DeleteCandidateButton
                        candidateActivity={candidateActivity}
                        jobDetails={props.jobDetails}
                        userSlice={props.userSlice}
                        styles={props.styles}
                        disabled={false}
                        onSuccess={onCandidateDeleted}
                      />

                      <TransferCandidateButton
                        candidateActivity={candidateActivity}
                        jobDetails={props.jobDetails}
                        clientId={clientId}
                        disabled={
                          [
                            K.CandidateStatus.Hired,
                            K.CandidateStatus.Terminated,
                            K.CandidateStatus.Withdrew,
                            K.CandidateStatus.Declined,
                          ].includes(props.statusHistory.previousStatus) ||
                          (props.jobDetails?.isCancel ?? true) ||
                          candidateActivity.isConsiderable === 0
                        }
                        onSuccess={onCandidateTransferred}
                      />
                    </Space>
                  )}
                />
              </Col>
            )}
          </Row>
        )}
      </div>

      <Modal
        centered
        width={462}
        title="Cancel Job"
        className={styles.jobCancelModal}
        open={modalVisible.isJobCancelModalVisible}
        onCancel={handleCancelJobCancel}
        closeIcon={<i className={`icon-closeable ${styles.closeAble}`} />}
        footer={
          <>
            <Button
              className={styles.jobCancelButton}
              onClick={handleCancelJobCancel}
            >
              Cancel
            </Button>
            <Button
              loading={isLoading}
              onClick={cancelJobForm.submit}
              className={styles.jobConfirmButton}
              disabled={!jobData.moveCandidates}
            >
              Confirm
            </Button>
          </>
        }
      >
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <div>
            {" "}
            <Title level={5} className={styles.jobCancelSubTitle}>
              <i
                className={`icon-vacancies-briefcase ${styles.briefcaseIcon}`}
              />
              Current Openings:{" "}
              {jobData.jobDetails?.vacancies?.currentOpenings ?? 0}
            </Title>
          </div>
          <div>
            <Title level={5} className={styles.jobCancelSubTitle}>
              <i className={`icon-user ${styles.briefcaseIcon}`} />
              No. of Submittals: {jobData.jobDetails?.submittedCount ?? 0}
            </Title>
          </div>
        </div>
        <CancelJobForm
          jobId={jobId}
          clientId={clientId}
          form={cancelJobForm}
          styles={styles}
          jobDetails={jobData.jobDetails}
          setJobData={setJobData}
          setIsLoading={setIsLoading}
          handleOkCancelJob={handleOkCancelJob}
        />
      </Modal>
      {/* Submit Cancel Billing  */}
      <Modal
        centered
        width={462}
        title="Submit Cancel Billings"
        className={`${styles.jobCancelModal} cancelBilling`}
        open={modalVisible.isCancelBillingModalVisible}
        onCancel={handleCancelBilling}
        closeIcon={<i className={`icon-closeable ${styles.closeAble}`} />}
        footer={
          <>
            <Button
              className={styles.jobCancelButton}
              onClick={handleCancelBilling}
            >
              Cancel
            </Button>
            <Button
              type="primary"
              loading={isLoading}
              onClick={cancelBillingForm.submit}
            >
              Confirm
            </Button>
          </>
        }
      >
        <Title level={5} className={styles.jobCancelSubTitle}>
          <i className={`icon-user${styles.briefcaseIcon}`} />
          No. of Submittals: {jobData.jobDetails?.submittedCount ?? 0}
        </Title>

        <SubmitCancelBillingForm
          jobId={jobId}
          clientId={clientId}
          form={cancelBillingForm}
          styles={styles}
          setIsLoading={setIsLoading}
          handleCancelBillingOk={handleCancelBillingOk}
        />
      </Modal>

      {modalVisible.isCandidateStatusChangeModalVisible && (
        <ChangeCandidateStatus
          selectedData={selectedCandidate}
          setSelectedData={setSelectedCandidate}
          isModalVisible={modalVisible}
          setIsModalVisible={setModalVisible}
          refreshTable={refreshCandidateListingTable}
        />
      )}
      <Drawer
        title="Settings"
        placement="right"
        onClose={onClose}
        open={modalVisible.isSettingModalVisible}
        className="settings-drawer-wrap"
      >
        <JobSettingModal
          jobId={jobId}
          jobData={jobData}
          setJobData={setJobData}
          vacancies={{
            ...jobData.settings,
            history: orderBy(jobData.settings?.history, "id", "desc"),
          }}
          setModalVisible={setModalVisible}
        />
      </Drawer>
    </>
  );
}
